import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

// Austrian RKSV standard VAT rates staff can pick from for an ad-hoc booking.
const VAT_RATES = [10, 13, 20];

// Gastronovi's "Diverse Buchung": a free-text/free-price line for anything not
// in the product catalog. Warengruppe (category) is mandatory so VAT reporting
// stays correct even for an ad-hoc item — step 1 picks the category tile,
// step 2 fills in description/price/quantity.
export default function DiverseBuchungDialog({ open, onClose, categories = [], onConfirm }) {
  const [category, setCategory] = useState(null);
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [vatRate, setVatRate] = useState(10);
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCategory(null);
      setDescription('');
      setPrice('');
      setVatRate(10);
      setQuantity(1);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!description.trim()) {
      toast.error('Bitte eine Bezeichnung eingeben');
      return;
    }
    const numericPrice = parseFloat(price);
    if (Number.isNaN(numericPrice) || numericPrice < 0) {
      toast.error('Bitte einen gültigen Preis eingeben');
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm({
        categoryId: category.id,
        description: description.trim(),
        price: numericPrice,
        vatRate,
        quantity,
      });
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="pos-workflow-dialog max-w-md mx-4 rounded-none border">
        <DialogHeader>
          <DialogTitle>
            {category ? category.name : 'Diverses'}
          </DialogTitle>
        </DialogHeader>

        {!category ? (
          <div className="grid grid-cols-3 gap-2 pt-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategory(cat)}
                className="flex h-16 items-center justify-center border border-white/30 px-2 text-center text-xs font-semibold text-white active:opacity-80"
                style={{ backgroundColor: cat.color || '#334155' }}
              >
                {cat.name}
              </button>
            ))}
            {categories.length === 0 && (
              <p className="col-span-3 text-sm text-muted-foreground">Keine Warengruppen gefunden.</p>
            )}
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <button
              type="button"
              onClick={() => setCategory(null)}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Warengruppe ändern
            </button>

            <div>
              <Label className="text-sm font-medium">Bezeichnung</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="z. B. Pilzrahmsauce"
                className="pos-dialog-field h-11 rounded-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Preis (€)</Label>
                <Input
                  type="number" min={0} step={0.5} value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  className="pos-dialog-field h-11 rounded-none"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Menge</Label>
                <Input
                  type="number" min={1} value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="pos-dialog-field h-11 rounded-none"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">MwSt.</Label>
              <div className="mt-1.5 flex gap-2">
                {VAT_RATES.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => setVatRate(rate)}
                    className={`flex-1 h-10 border font-medium ${
                      vatRate === rate ? 'border-emerald-600 bg-emerald-600 text-white' : 'pos-dialog-secondary'
                    }`}
                  >
                    {rate}%
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="pos-dialog-primary w-full h-12 rounded-none"
            >
              Hinzufügen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
